import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { NONE_VALUE } from '@/constants/answers';
import type { AppropriatenessTest, AppropriatenessTestAverageComplexTransactionValue } from '@/features/futures/schemas/appropriateness-test';
import type { FormStepGuard } from '@/hooks/use-form-step-guard';
import { usePromise } from '@/hooks/use-promise';
import { FUTURES_APPROPRIATENESS_AVERAGE_COMPLEX_TRANSACTION_VALUE_ANSWERS } from '@/services/api/profile/futures-appropriateness';

const FuturesAppropriatenessTestFieldsetAverageComplexTransactionValueLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('FuturesAppropriatenessAssessment.averageComplexTransactionValue.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FuturesAppropriatenessAssessment.averageComplexTransactionValue.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
FuturesAppropriatenessTestFieldsetAverageComplexTransactionValueLegend.displayName = 'FuturesAppropriatenessTestFieldsetAverageComplexTransactionValueLegend';

const FuturesAppropriatenessTestFieldsetAverageComplexTransactionValue = () => {
  const form = useFormContext<Partial<AppropriatenessTestAverageComplexTransactionValue>>();
  const { nextStepDeferred } = useGuardedFormStepper();
  const { isPending, handler: handleNextStep } = usePromise(() => nextStepDeferred({ submit: true }));
  const { t } = useTranslation();

  return (
    <FormField
      name="averageComplexTransactionValue"
      control={form.control}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="sr-only">{t('FuturesAppropriatenessAssessment.averageComplexTransactionValue.title')}</FormLabel>
          <FormControl>
            <RadioGroup
              className="!mt-0"
              defaultValue={field.value}
              variant="cards"
              aria-label={t('FuturesAppropriatenessAssessment.averageComplexTransactionValue.title')}
              onValueChange={(value) => {
                field.onChange(value);
                handleNextStep();
              }}
            >
              {FUTURES_APPROPRIATENESS_AVERAGE_COMPLEX_TRANSACTION_VALUE_ANSWERS.map((answer) => (
                <FormItem key={answer}>
                  <RadioGroupItemCard variant="outline">
                    <FormControl>
                      <RadioGroupItem value={answer} aria-disabled={isPending} />
                    </FormControl>
                    <FormLabel contained onClick={handleNextStep}>
                      {t(`FuturesAppropriatenessAssessment.averageComplexTransactionValue.options.${answer}`)}
                    </FormLabel>
                  </RadioGroupItemCard>
                </FormItem>
              ))}
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
FuturesAppropriatenessTestFieldsetAverageComplexTransactionValue.displayName = 'FuturesAppropriatenessTestFieldsetAverageComplexTransactionValue';
FuturesAppropriatenessTestFieldsetAverageComplexTransactionValue.isAutoNextStep = true;

const beforeStepEnter: FormStepGuard<AppropriatenessTest> = async ({ direction, form, steps }, next) => {
  if (!form) return next(false);

  const complexTransactionCount = form.getValues('complexTransactionCount');

  if (complexTransactionCount === NONE_VALUE) return next(steps.indexOf(FuturesAppropriatenessTestFieldsetAverageComplexTransactionValue) + (direction === 'next' ? 1 : -1));

  return next();
};

FuturesAppropriatenessTestFieldsetAverageComplexTransactionValue.beforeStepEnter = beforeStepEnter;

export { FuturesAppropriatenessTestFieldsetAverageComplexTransactionValue, FuturesAppropriatenessTestFieldsetAverageComplexTransactionValueLegend };
